import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { OneHourInMillis } from '@racemap/utilities/consts/time';
import { Space } from 'antd';
import { HelperModal } from '../Modal';

export type TimeFrames = {
  searchWindowStart: number;
  searchWindowEnd: number;
  eventStart: number | null;
  eventEnd: number | null;
  resultWindowStart: number | null;
  resultWindowEnd: number | null;
};

type TimeFramesIndicatorProps = {
  timeFrames: TimeFrames;
};

const GapBreakThresholdInMillis = 24 * OneHourInMillis;

function formatDurationShort(durationInMillis: number): string {
  if (!Number.isFinite(durationInMillis) || durationInMillis <= 0) return '0m';

  const totalMinutes = Math.floor(durationInMillis / (60 * 1000));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function isValidTimestamp(value: number | null | undefined): value is number {
  return value != null && Number.isFinite(value);
}

function getResultFrame(
  timeFrames: TimeFrames,
  minTime: number,
  scaleFactor: number,
  leftBorder: number,
): { startInPixel: number; widthInPixel: number } | null {
  if (
    !isValidTimestamp(timeFrames.resultWindowStart) ||
    !isValidTimestamp(timeFrames.resultWindowEnd)
  ) {
    return null;
  }
  const startInPixel = (timeFrames.resultWindowStart - minTime) * scaleFactor + leftBorder;
  const endInPixel = (timeFrames.resultWindowEnd - minTime) * scaleFactor + leftBorder;
  if (!Number.isFinite(startInPixel) || !Number.isFinite(endInPixel)) return null;
  return {
    startInPixel,
    widthInPixel: Math.max(endInPixel - startInPixel, 2),
  };
}

function getEventFrame(
  timeFrames: TimeFrames,
  minTime: number,
  scaleFactor: number,
  leftBorder: number,
): { startInPixel: number; widthInPixel: number } | null {
  if (!isValidTimestamp(timeFrames.eventStart) || !isValidTimestamp(timeFrames.eventEnd)) {
    return null;
  }
  const startInPixel = (timeFrames.eventStart - minTime) * scaleFactor + leftBorder;
  const endInPixel = (timeFrames.eventEnd - minTime) * scaleFactor + leftBorder;
  if (!Number.isFinite(startInPixel) || !Number.isFinite(endInPixel)) return null;
  return {
    startInPixel,
    widthInPixel: Math.max(endInPixel - startInPixel, 2),
  };
}

function TimeFramesIndicator({ timeFrames }: TimeFramesIndicatorProps) {
  const maxPixelWidth = 1000;
  const leftBorder = 50;
  const now = Date.now();

  const searchWindowStart = isValidTimestamp(timeFrames.searchWindowStart)
    ? timeFrames.searchWindowStart
    : now - OneHourInMillis;
  const searchWindowEnd = isValidTimestamp(timeFrames.searchWindowEnd)
    ? timeFrames.searchWindowEnd
    : now;

  const maxRelevantTime = Math.max(
    searchWindowEnd,
    isValidTimestamp(timeFrames.eventEnd) ? timeFrames.eventEnd : Number.NEGATIVE_INFINITY,
    isValidTimestamp(timeFrames.resultWindowEnd)
      ? timeFrames.resultWindowEnd
      : Number.NEGATIVE_INFINITY,
  );
  const gapToNowInMillis = Math.max(now - maxRelevantTime, 0);
  const hasGapBreak = gapToNowInMillis > GapBreakThresholdInMillis;

  const maxCandidates = [
    searchWindowEnd,
    isValidTimestamp(timeFrames.eventEnd) ? timeFrames.eventEnd : null,
    isValidTimestamp(timeFrames.resultWindowEnd) ? timeFrames.resultWindowEnd : null,
    hasGapBreak ? null : now,
  ].filter(isValidTimestamp);

  const minCandidates = [
    searchWindowStart,
    isValidTimestamp(timeFrames.eventStart) ? timeFrames.eventStart : null,
    isValidTimestamp(timeFrames.resultWindowStart) ? timeFrames.resultWindowStart : null,
  ].filter(isValidTimestamp);

  const maxTime = Math.max(...maxCandidates) + OneHourInMillis;
  const minTime = Math.min(...minCandidates) - OneHourInMillis;

  // Layout configuration for the right-hand side and gap-break section.
  // These offsets are chosen so that, for the current maxPixelWidth, they
  // reproduce the previous absolute positions (860, 895, 940, 950).
  const rightLabelMargin = 50; // distance from SVG right edge to rightmost data X
  const breakWidth = 35; // width of the visual break segment
  const breakToNowGap = 45; // gap between end of break and start of "now" in break mode
  const nowToRightPadding = 10; // padding between "now" start and rightmost data X
  const gapBeforeBreak = 8; // extra gap between data region and break start when a break is shown

  const baseDataRightX = maxPixelWidth - rightLabelMargin;
  const nowStartInBreakMode = baseDataRightX - nowToRightPadding;
  const breakEndX = nowStartInBreakMode - breakToNowGap;
  const breakStartX = breakEndX - breakWidth;
  const dataRightX = hasGapBreak ? breakStartX - gapBeforeBreak : baseDataRightX;
  const drawPixelWidth = Math.max(dataRightX - leftBorder, 1);

  const timeFrameWidthInMillis = Math.max(maxTime - minTime, 1);
  const scaleFactor = drawPixelWidth / timeFrameWidthInMillis;

  const convertTimeToPixel = (timestamp: number): number => {
    const pixel = (timestamp - minTime) * scaleFactor + leftBorder;
    return Number.isFinite(pixel) ? pixel : leftBorder;
  };

  const searchWindowStartInPixel = convertTimeToPixel(searchWindowStart);
  const searchWindowEndInPixel = convertTimeToPixel(searchWindowEnd);

  const nowStartInPixel = hasGapBreak ? nowStartInBreakMode : convertTimeToPixel(now);

  const resultFrame = getResultFrame(timeFrames, minTime, scaleFactor, leftBorder);
  const eventFrame = getEventFrame(timeFrames, minTime, scaleFactor, leftBorder);

  const legentTextYPosition = 27;
  const showFrame = false;

  return (
    // should retrun an svg showing the timeframes in as horzontal lines the with of the svg should be bigger as the searchwindow
    // on th ebottom there should be a line like in a chart just representing the time. using the mouse to hover the frames should give the exact time as a tooltip
    <TimeFrameContainer>
      <svg
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        viewBox="0 0 1000 30"
      >
        <defs>
          <marker
            id="arrow"
            markerWidth="10"
            markerHeight="10"
            refX="5.7"
            refY="3"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M0,1 L0,5 L7,3 z" />
          </marker>
          <marker
            id="bar"
            markerWidth="10"
            markerHeight="10"
            refX="0.5"
            refY="1"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M0,0 L0,2 L0.5,2 L0.5,0z" />
          </marker>
        </defs>
        <title>Timeframe Indicator</title>
        {showFrame && (
          <rect
            id="frame"
            x="0"
            y="0"
            width="1000"
            height="30"
            fill="none"
            stroke="black"
            strokeWidth="0.5"
          />
        )}
        <g>
          <title>Now »</title>
          <text fontSize="6" x={nowStartInPixel - 20} y={legentTextYPosition} fill="black">
            Now »
          </text>
          <line
            id="nowLine"
            x1={nowStartInPixel}
            y1="0"
            x2={nowStartInPixel}
            y2="40"
            stroke="#707070"
            strokeWidth="1px"
          />
        </g>
        <g>
          <title>Timeline</title>
          {!hasGapBreak && (
            <line
              id="baseLine"
              x1={10}
              y1="20"
              x2={maxPixelWidth - 10}
              y2="20"
              stroke="#707070"
              strokeWidth="1"
              strokeLinecap="round"
              markerEnd="url(#arrow)"
            />
          )}
          {hasGapBreak && (
            <>
              <line
                id="baseLineLeft"
                x1={10}
                y1="20"
                x2={breakStartX}
                y2="20"
                stroke="#707070"
                strokeWidth="1"
                strokeLinecap="round"
              />
              <line
                id="baseLineRight"
                x1={breakEndX}
                y1="20"
                x2={maxPixelWidth - 10}
                y2="20"
                stroke="#707070"
                strokeWidth="1"
                strokeLinecap="round"
                markerEnd="url(#arrow)"
              />
              <line
                x1={breakStartX + 7}
                y1="16"
                x2={breakStartX + 13}
                y2="24"
                stroke="#707070"
                strokeWidth="1"
              />
              <line
                x1={breakStartX + 16}
                y1="16"
                x2={breakStartX + 22}
                y2="24"
                stroke="#707070"
                strokeWidth="1"
              />
              <text fontSize="6" x={breakStartX - 4} y="13" fill="#707070">
                {formatDurationShort(gapToNowInMillis)}
              </text>
            </>
          )}
          <text fontSize="6" x={975} y={legentTextYPosition} fill="black">
            Time
          </text>
        </g>
        <g>
          <title>Timeframe covering all shown results</title>
          {resultFrame && (
            <g>
              <rect
                x={resultFrame.startInPixel}
                y="5"
                width={resultFrame.widthInPixel}
                height="13"
                fill={RacemapColors.LightGreen}
                // fill="#00FF00a0"
                stroke="green"
                strokeWidth="0"
              />
              <line
                x1={resultFrame.startInPixel}
                y1="3"
                x2={resultFrame.startInPixel}
                y2="19"
                // stroke="#1d851d"
                stroke={RacemapColors.BaseGreen}
                strokeWidth="1"
              />
              <line
                x1={resultFrame.startInPixel + resultFrame.widthInPixel}
                y1="3"
                x2={resultFrame.startInPixel + resultFrame.widthInPixel}
                y2="19"
                stroke={RacemapColors.BaseGreen}
                // stroke="#1d851d"
                strokeWidth="1"
              />
            </g>
          )}
        </g>
        <g>
          <title>Time Frame for search</title>
          <line
            id="searchFrame"
            x1={searchWindowStartInPixel}
            y1="15"
            x2={searchWindowEndInPixel}
            y2="15"
            stroke={RacemapColors.PaleBlue}
            strokeWidth="2"
            markerEnd="url(#bar)"
            markerStart="url(#bar)"
          />
        </g>
        <g>
          <title>Duration of the event</title>
          {eventFrame && (
            <line
              id="eventDuration"
              x1={eventFrame.startInPixel}
              y1="8"
              x2={eventFrame.startInPixel + eventFrame.widthInPixel}
              y2="8"
              stroke={RacemapColors.BaseRed}
              strokeWidth="2"
              markerEnd="url(#bar)"
              markerStart="url(#bar)"
            />
          )}
        </g>
      </svg>
      <MoveLeftContainer>
        <HelperModal title="Show the time frames of your selection" size="lg">
          <Space orientation="vertical">
            Each bar of the Time Frame Indicator represents a time frame. The purpose of showing
            each frame is to get a better understanding of the event-time-fame in relation to the
            search window and the result.
            <Space>
              <ColoredSquare color={RacemapColors.PaleBlue} />
              <span> The blue bar shows the time frame of the search.</span>
            </Space>
            <Space>
              <ColoredSquare color={RacemapColors.BaseRed} />
              <span> The red bar shows the duration of the event.</span>
            </Space>
            {resultFrame && (
              <Space>
                <ColoredSquare color={RacemapColors.LightGreen} />
                <span> The green bar shows the time frame of the results.</span>
              </Space>
            )}
          </Space>
        </HelperModal>
      </MoveLeftContainer>
    </TimeFrameContainer>
  );
}

const MoveLeftContainer = styled.div`
  margin-left: -30px;
`;

const TimeFrameContainer = styled.div`
  display: flex;
  width: 100%;  
  margin-bottom: 10px;
`;

const ColoredSquare = styled.div`
  width: 18px;
  height: 18px;
  background-color: ${({ color }) => color};
`;

export default TimeFramesIndicator;
